import CheckIcon from '@mui/icons-material/Check';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { type AdvertiserAccountStatus, AdvertiserAccountStatusSchema } from '@q4udoc/types';
import {
  Button,
  Datagrid,
  DateField,
  EmailField,
  List,
  SelectInput,
  Show,
  SimpleShowLayout,
  TextField,
  TopToolbar,
  useRecordContext,
} from 'react-admin';
import { CentsField } from '../components/CentsField';
import { RejectButton } from '../components/RejectButton';
import { useResourceAction } from '../hooks/useResourceAction';

// Ids come from the shared enum so a new status cannot be missed here; the labels stay local.
const STATUS_LABELS: Record<AdvertiserAccountStatus, string> = {
  pending: 'Pending Review',
  active: 'Active',
  suspended: 'Suspended',
  rejected: 'Rejected',
};
const statusChoices = AdvertiserAccountStatusSchema.options.map((id) => ({
  id,
  name: STATUS_LABELS[id],
}));

export const AdvertiserAccountList = () => (
  <List
    filters={[<SelectInput key="status" source="status" choices={statusChoices} label="Status" />]}
    empty={
      <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Typography variant="h6" gutterBottom>
          No Advertiser Accounts Found
        </Typography>
      </Box>
    }
  >
    <Datagrid rowClick="show" bulkActionButtons={false}>
      <TextField source="name" />
      <TextField source="companyName" />
      <EmailField source="contactEmail" />
      <TextField source="status" />
      <CentsField source="balanceCents" label="Balance" />
      <DateField source="createdAt" />
    </Datagrid>
  </List>
);

const ApproveButton = () => {
  const record = useRecordContext();
  const approve = useResourceAction('advertiser-accounts', 'approve', {
    success: 'Advertiser approved successfully',
    error: 'Error approving advertiser',
  });
  if (record?.status !== 'pending') return null;
  return (
    <Button label="Approve" onClick={() => approve()} startIcon={<CheckIcon />} color="success" />
  );
};

const AdvertiserAccountShowActions = () => (
  <TopToolbar>
    <ApproveButton />
    <RejectButton
      resource="advertiser-accounts"
      pendingStatus="pending"
      title="Reject Advertiser Application"
      helperText="Please explain why this application is being rejected..."
      success="Advertiser rejected"
      error="Error rejecting advertiser"
    />
  </TopToolbar>
);

export const AdvertiserAccountShow = () => (
  <Show actions={<AdvertiserAccountShowActions />}>
    <SimpleShowLayout>
      <TextField source="name" />
      <TextField source="companyName" />
      <EmailField source="contactEmail" />
      <TextField source="website" />
      <TextField source="status" />
      <CentsField source="balanceCents" label="Balance" />
      <TextField source="rejectionReason" />
      <DateField source="approvedAt" />
      <TextField source="approvedBy" />
      <DateField source="createdAt" />

      <Typography variant="h6" sx={{ mt: 2 }}>
        Application Details
      </Typography>
      {useRecordContext()?.applicationData && (
        <>
          <TextField source="applicationData.companyName" label="Company Name" />
          <TextField source="applicationData.contactName" label="Contact Name" />
          <EmailField source="applicationData.contactEmail" label="Contact Email" />
          <TextField source="applicationData.website" label="Website" />
          <TextField source="applicationData.industry" label="Industry" />
          <TextField source="applicationData.adBudgetRange" label="Ad Budget Range" />
          <TextField source="applicationData.description" label="Description" />
        </>
      )}
    </SimpleShowLayout>
  </Show>
);
